import React from 'react';
import { Plus, Pencil, Save, GripVertical, Search, List, Table, Link2, GripHorizontal, ArrowDownWideNarrow, ChevronLeft } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

/**
 * STUI page tabs for the dynamic module's anatomy and the notification system.
 *
 * Dynamic Module: one composed, annotated mock of the standard list/detail screen (module
 * header, list panel, detail panel, related tables) with lettered regions, plus the region,
 * header-behavior, column-span and field-type references. Every piece is real STUI markup;
 * .stui-ssg-region / -subregion are annotation overlays only (dashed outline + floating label,
 * no layout of their own). The individual parts are documented in depth on Standard View.
 *
 * Notifications: the two channels (header alerts, footer status), every notify.* call, the
 * multi-alert behavior, and live buttons that fire the real notifications.
 */

const REGION_ROWS = [
  ['A', '.stui-module-header', 'Module header: view toggle, record count, CRUD, search, 6-dot menu'],
  ['B', '.stui-list-panel', 'Collapsible record list. Items show primary + secondary text; search lives in the module header.'],
  ['C', '.stui-detail-panel', 'Selected record detail with scrollable form content; .stui-detail-empty when nothing is selected'],
  ['C1', '.stui-detail-section-header', 'Section bar (bar color) separating field groups'],
  ['C2', '.stui-detail-grid', '24-column grid. Fields use .stui-field-group.stui-col-N'],
  ['D', '.stui-related-section', 'Related data below the detail. Header (resizable, collapsible) + tabs + FilterableDataGrid.'],
  ['E', '.stui-datasheet-view', 'Full-width grid view. Replaces B + C when the datasheet is active.'],
];

const SPAN_ROWS = [
  ['.stui-col-4', '~17%', 'Small fields (status, boolean, short code)'],
  ['.stui-col-6', '25%', 'Quarter-width fields'],
  ['.stui-col-8', '33%', 'Standard fields (name, email, date)'],
  ['.stui-col-12', '50%', 'Half-width fields (description, address)'],
  ['.stui-col-24', '100%', 'Full width (textarea, section header, notes)'],
];

const FIELD_ROWS = [
  ['Text', '.stui-field-input', 'Standard text input, --input-height tall'],
  ['Readonly', '.stui-field-input[readonly]', 'Not editable in the current mode'],
  ['Derived', '.stui-field-input.derived', 'Computed / immutable field (muted + italic)'],
  ['Dirty', '.stui-field-input.dirty', 'Changed since the last save (dirty fill + border)'],
  ['Select', 'select.stui-field-input', 'Dropdown'],
  ['Textarea', 'textarea.stui-field-input', 'Multi-line'],
  ['FK', '.stui-field-input.fk', 'Opens the FK record popup'],
  ['FK link', '.stui-field-input.fk-link', 'Navigates to the related record'],
  ['Checkbox', '.stui-checkbox + .stui-checkbox-input', 'Flex row wrapper around the themed STUI box'],
  ['Section Header', '.stui-detail-section-header', 'Full-width bar, spans .stui-col-24'],
  ['Spacer', '.stui-col-N (empty div)', 'Empty grid cell for layout control'],
];

const RefTable = ({ head, rows }) => (
  <table className="stui-ssg-reftable">
    <thead><tr>{head.map(h => <th key={h}>{h}</th>)}</tr></thead>
    <tbody>
      {rows.map(r => (
        <tr key={r[0] + r[1]}>
          <td><strong>{r[0]}</strong></td>
          <td><code>{r[1]}</code></td>
          <td>{r[2]}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Field = ({ span, label, value, cls = '' }) => (
  <div className={`stui-field-group stui-col-${span}`}>
    <label>{label}</label>
    <input type="text" value={value} readOnly className={`stui-field-input ${cls}`.trim()} />
  </div>
);

export const DynamicModuleTab = () => (
  <div className="stui-preview-zone stui-tab-body">
    <div className="stui-module-container stui-ssg-anatomy">
      <div className="stui-ssg-region">
        <div className="stui-ssg-region-label">A: MODULE HEADER</div>
        <div className="stui-module-header">
          <div className="stui-module-header-left">
            <div className="stui-module-header-tabs">
              <button className="stui-tab-btn-icon active" title="List View"><List size={18} /></button>
              <button className="stui-tab-btn-icon" title="Datasheet View"><Table size={18} /></button>
            </div>
            <span className="stui-text-xs stui-text-muted">5 of 100 records</span>
          </div>
          <div className="stui-module-header-actions">
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" title="Add new"><Plus size={18} /></button>
              <button className="stui-crud-btn disabled" disabled title="Edit"><Pencil size={18} /></button>
              <button className="stui-crud-btn disabled" disabled title="Save"><Save size={18} /></button>
            </div>
            <div className="stui-search-wrapper">
              <Search size={14} className="stui-search-icon" />
              <input className="stui-search-input" placeholder="Search..." readOnly />
            </div>
            <div className="stui-action-menu">
              <button className="stui-action-menu-btn" title="Actions"><GripVertical size={16} /></button>
            </div>
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        <div className="stui-ssg-region">
          <div className="stui-ssg-region-label">B: LIST PANEL</div>
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button className="stui-list-panel-collapse-btn" title="Collapse list"><ChevronLeft size={16} /></button>
            </div>
            <div className="stui-list-scroll">
              {[1, 2, 3, 4, 5].map(i => (
                <button key={i} className={`stui-list-item ${i === 1 ? 'selected' : ''}`}>
                  <span className="stui-list-item-primary">Sample Record {i}</span>
                  <span className="stui-list-item-secondary">Secondary info · metadata</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="stui-ssg-region stui-ssg-region-grow">
          <div className="stui-ssg-region-label">C: DETAIL PANEL</div>
          <div className="stui-detail-panel">
            <div className="stui-detail-content">
              <div className="stui-ssg-subregion">
                <div className="stui-ssg-subregion-label">C1: Section Header</div>
                <div className="stui-detail-grid">
                  <div className="stui-col-24"><div className="stui-detail-section-header">General Information</div></div>
                </div>
              </div>

              <div className="stui-ssg-subregion">
                <div className="stui-ssg-subregion-label">C2: 24-Column Form Grid</div>
                <div className="stui-detail-grid">
                  <Field span={8} label="stui-col-8 (third)" value="Standard field" />
                  <Field span={8} label="stui-col-8" value="Standard field" />
                  <Field span={8} label="stui-col-8" value="Standard field" />
                  <Field span={12} label="stui-col-12 (half)" value="Half width field" />
                  <Field span={12} label="stui-col-12" value="Half width field" />
                  <div className="stui-field-group stui-col-6">
                    <label>FK Field</label>
                    <div className="stui-field-input fk" role="button" tabIndex={0}>
                      <span className="stui-field-fk-value">Related Record</span>
                      <Link2 size={12} className="stui-field-fk-icon" />
                    </div>
                  </div>
                  <Field span={6} label="Derived" value="Computed" cls="derived" />
                  <Field span={6} label="Dirty Field" value="Changed!" cls="dirty" />
                  <div className="stui-field-group stui-col-6">
                    <label className="stui-checkbox">
                      <input type="checkbox" className="stui-checkbox-input" checked readOnly />
                      <span>Checkbox</span>
                    </label>
                  </div>
                  <div className="stui-field-group stui-col-24">
                    <label>stui-col-24 (full width)</label>
                    <textarea value="Full width textarea field" readOnly className="stui-field-input" rows={2} />
                  </div>
                </div>
              </div>

              <div className="stui-ssg-subregion stui-mt-md">
                <div className="stui-ssg-subregion-label">D: RELATED TABLES</div>
                <div className="stui-related-section stui-ssg-anatomy-related">
                  <div className="stui-related-header resizable">
                    <div className="stui-resize-grip"><GripHorizontal size={14} /></div>
                    <span>Related Information</span>
                    <button className="stui-related-collapse-btn" title="Collapse"><ArrowDownWideNarrow size={16} /></button>
                  </div>
                  <div className="stui-related-header">
                    <div className="stui-related-tabs">
                      <button className="stui-related-tab active">Tab 1 <span className="stui-related-tab-count">(3)</span></button>
                      <button className="stui-related-tab">Tab 2</button>
                    </div>
                  </div>
                  <div className="stui-related-content">
                    <div className="stui-related-body">FilterableDataGrid renders here</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="stui-ssg-section stui-mt-lg">
      <div className="stui-detail-section-header">Region Reference</div>
      <RefTable head={['Region', 'CSS Class', 'Description']} rows={REGION_ROWS} />

      <div className="stui-detail-section-header stui-ssg-subsection">Header Behavior</div>
      <table className="stui-ssg-reftable">
        <thead><tr><th>View</th><th>Left Side</th><th>Right Side</th></tr></thead>
        <tbody>
          <tr><td><strong>List</strong></td><td>List icon (active) → Table icon → X of Y</td><td>+ / Trash → Pencil / X → Save → Search → 6-dot (Refresh)</td></tr>
          <tr><td><strong>Datasheet</strong></td><td>List icon → Table icon (active) → X of Y</td><td>+ → Pencil / X → Save → Search → 6-dot (Refresh, Download, Modify View, Bulk Delete)</td></tr>
        </tbody>
      </table>

      <div className="stui-detail-section-header stui-ssg-subsection">Detail Panel Column Spans</div>
      <p>Fields sit in a 24-column grid. Common spans:</p>
      <RefTable head={['Span', 'Width', 'Typical Use']} rows={SPAN_ROWS} />

      <div className="stui-detail-section-header stui-ssg-subsection">Field Types</div>
      <RefTable head={['Type', 'Class / Behavior', 'Description']} rows={FIELD_ROWS} />

      <div className="stui-detail-section-header stui-ssg-subsection">Related Tables</div>
      <ul>
        <li>Sits below the detail form in the detail panel</li>
        <li>Header with resize grip and collapse toggle; optional CRUD icons</li>
        <li>Tab bar for multiple related tables (e.g. Drawing Log, IP Addresses), each with a count</li>
        <li>Each tab loads a FilterableDataGrid with the related records</li>
        <li>Double-click a row to open the FK popup for that record</li>
      </ul>
      <p>Each part is documented in depth on the Standard View tab; open any data module (Buildings, Drawings…) to see it live.</p>
    </div>
  </div>
);

const STACK_MESSAGES = [
  'Database connection timed out.', 'Some fields failed validation.',
  'Save failed — record is locked by another user.', 'Session will expire in 5 minutes.',
  'Sync conflict detected on Tags table.', 'Permission denied on Buildings endpoint.',
  'FK reference missing — check Organization field.', 'API rate limit approaching.',
  'Draft changes not saved.', 'Network timeout on report export.', 'Schema mismatch detected.',
  'User impersonation session active.', 'Stale data warning — last refresh over 10 minutes ago.',
  'Export queue is full.', 'Duplicate record detected.', 'Backend returned 503.',
  'Token refresh failed.', 'File size exceeds limit.', 'Required field missing.',
  'Batch operation partially failed.',
];

export const NotificationsTab = () => {
  const { notify } = useNotifications();

  const examples = [
    { label: 'Error', cls: 'stui-btn-danger', action: () => notify.error('Something went wrong — this requires acknowledgement.') },
    { label: 'Warning', cls: 'stui-btn-secondary', action: () => notify.warning('Heads up — this will auto-dismiss in 5 seconds.') },
    { label: 'Confirm', cls: 'stui-btn-primary', action: () => notify.confirm('Delete this record? This cannot be undone.', () => notify.success('Record deleted.'), { confirmText: 'Delete', cancelText: 'Cancel' }) },
    { label: 'Success (footer)', cls: 'stui-btn-secondary', action: () => notify.success('Record saved successfully.') },
    { label: 'Info (footer)', cls: 'stui-btn-secondary', action: () => notify.info('Background sync completed — 142 records updated.') },
    { label: 'Persistent Status', cls: 'stui-btn-secondary', action: () => notify.status('Syncing data…', 0) },
    { label: 'Clear Status', cls: 'stui-btn-secondary', action: () => notify.clearStatus() },
  ];

  const stack = (n) => {
    for (let i = 0; i < n; i++) {
      const msg = STACK_MESSAGES[i % STACK_MESSAGES.length];
      if (i % 2 === 0) notify.error(msg); else notify.warning(msg);
    }
  };

  return (
    <div className="stui-preview-zone stui-tab-body">
      <div className="stui-ssg-section">
        <div className="stui-detail-section-header">Notification System</div>
        <p>Two channels: <strong>header alerts</strong> (top center) for anything needing attention, <strong>footer status</strong> (bottom left) for transient acknowledgements. Never use window.confirm or alert.</p>

        <table className="stui-ssg-reftable stui-mt-md">
          <thead><tr><th>Call</th><th>Channel</th><th>Auto-dismiss</th><th>Use for</th></tr></thead>
          <tbody>
            <tr><td><code>notify.error(msg)</code></td><td>Header</td><td>Never — requires X</td><td>API failures, blocking errors</td></tr>
            <tr><td><code>notify.warning(msg)</code></td><td>Header</td><td>5 seconds</td><td>Non-fatal issues, heads-up messages</td></tr>
            <tr><td><code>notify.confirm(msg, onConfirm, opts)</code></td><td>Header</td><td>Never — confirm / cancel buttons</td><td>Destructive action confirmations</td></tr>
            <tr><td><code>notify.success(msg)</code></td><td>Footer</td><td>3 seconds</td><td>Save confirmations, completed actions</td></tr>
            <tr><td><code>notify.info(msg)</code></td><td>Footer</td><td>4 seconds</td><td>Background completions, quiet FYIs</td></tr>
            <tr><td><code>notify.status(msg, 0)</code></td><td>Footer</td><td>Never (persistent)</td><td>Loading / working states; clear with notify.clearStatus()</td></tr>
          </tbody>
        </table>

        <div className="stui-detail-section-header stui-ssg-subsection">Multi-Alert Behavior</div>
        <p>One alert renders normally. Two or more collapse to a summary row (expanded by default) with the worst severity icon, the alert count, Dismiss All and a collapse toggle. Each alert in the expanded stack keeps its own dismiss control.</p>

        <div className="stui-detail-section-header stui-ssg-subsection">Live Examples</div>
        <p>Fire these to see them in context. Stack several to see the collapse behavior.</p>
        <div className="stui-mt-md stui-row-10-wrap">
          {examples.map(({ label, cls, action }) => (
            <button key={label} type="button" className={`stui-btn stui-btn-sm ${cls} stui-text-nowrap`} onClick={action}>{label}</button>
          ))}
        </div>

        <div className="stui-detail-section-header stui-ssg-subsection">Stack Test</div>
        <p>Fires N alerts at once: watch the first-to-stack transition and the scrolling.</p>
        <div className="stui-mt-md stui-row-10-wrap">
          {[3, 5, 10, 20].map(n => (
            <button key={n} type="button" className="stui-btn stui-btn-sm stui-btn-primary stui-text-nowrap" onClick={() => stack(n)}>Stack {n}</button>
          ))}
        </div>
      </div>
    </div>
  );
};
